// apps/web/src/features/admin/automations/create/CreateAutomationPage.tsx
//
// E-062 — the automation create flow: WHERE → ON WHAT → IF/THEN → Review.
// Replaces the 5-step intent stepper (tiles retired, ruling 2). Every option
// the screens offer is DERIVED from the repaired effect registry through
// authoring-options.ts; unavailable choices render disabled WITH their reason
// (E-059), and a running rule-sentence accumulates on every screen.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  compileAutomation,
  describeAutomation,
  effectByKey,
  stageForStep,
  stepsForStage,
  STEP_LABELS,
  type Automation,
  type AutomationBranch,
  type AutomationBranchCheck,
  type AutomationBranchRoute,
  type ConditionGroup,
  type MomentId,
  type ScopeId,
  type Stage,
} from '@capture/shared';
import { FormErrorInline } from '@/components/FormErrorInline';
import { toast } from '@/components/Toaster';
import { useFormConfigStore } from '../../form-config/state/store';
import { usePipelineSteps } from '../../hooks/usePipelineSteps';
import { useJobTypeFields, summarizeFormFields, type FieldDefinitionSummary } from '../../hooks/useJobTypeFields';
import { useJobType } from '../../hooks/useJobTypes';
import { useLookupTables } from '../../hooks/useLookupTables';
import { useRoles } from '../../hooks/useRoles';
import { useUsers } from '../../hooks/useUsers';
import { ConditionGroupEditor } from '../../components/ConditionGroup';
import { CheckConfigBody, type RowCheckGroupOption } from '../CheckConfigBody';
import { RouteConfigBody } from '../RouteConfigBody';
import { IdentitySection } from '../panel/IdentitySection';
import { TryItSection } from '../panel/TryItSection';
import { emptyConditionGroup, isConditionEmpty } from '../effect-bucket';
import {
  whereOptions,
  scopeOptions,
  scopeCollapseHint,
  effectPalette,
  seedBranchesForEffects,
  seedConfigForEffect,
  type WhereOption,
} from './authoring-options';
import panelStyles from '../panel/panel.module.css';
import styles from './create.module.css';

const SCREEN_LABELS = ['Where', 'On what', 'If / Then', 'Review'] as const;
const S_WHERE = 0;
const S_SCOPE = 1;
const S_IFTHEN = 2;
const S_REVIEW = 3;

/** Pipeline step words that carry an explicit rule stage (mirrors the panel's
 *  WhereWhenSection) — the only steps offered as chips. */
const EDIT_CAPABLE_STEP_TYPES = new Set(['key', 'verify', 'qa', 'qc', 'supervisor']);

function rowKeyForMoment(moment: MomentId): string {
  return moment === 'batch_complete' ? 'gate' : moment;
}

function stageLabelList(stages: Stage[]): string {
  return stages
    .map((stage) => stepsForStage(stage).map((s) => STEP_LABELS[s]).join('/'))
    .join(' or ');
}

export function CreateAutomationPage() {
  const { jobTypeId = '' } = useParams<{ jobTypeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const versionId = useFormConfigStore((s) => s.version?.id ?? null);
  const replaceAutomationRules = useFormConfigStore((s) => s.replaceAutomationRules);
  const setPendingAutomationDraft = useFormConfigStore((s) => s.setPendingAutomationDraft);

  const [pickedFormId, setPickedFormId] = useState<string>(searchParams.get('form') ?? '');
  // F-056 — resolve the picked id against the REAL form list; an unknown id
  // (stale ?form= hand-off) resolves to the first form, which is exactly what
  // the form <select> displays. The rule is created in this form, so every
  // field vocabulary below derives from it.
  const selectedForm = contentV3?.forms.find((f) => f.id === pickedFormId) ?? contentV3?.forms[0];
  const formId = selectedForm?.id ?? '';

  const { steps: pipelineStepRows } = usePipelineSteps(jobTypeId);
  const pipelineSteps = useMemo(
    () => pipelineStepRows.map((s) => ({ stepType: s.stepType, label: s.label })),
    [pipelineStepRows],
  );
  const chipSteps = pipelineSteps.filter((s) => EDIT_CAPABLE_STEP_TYPES.has(s.stepType));
  const { fields: jobTypeFields } = useJobTypeFields(jobTypeId);
  const { jobType } = useJobType(jobTypeId);
  const { tables: lookupTables } = useLookupTables();
  const { roles: tenantRoles } = useRoles();
  const roleOptions = (tenantRoles ?? []).map((r) => ({ slug: r.slug, name: r.name }));
  const { users: tenantUsers } = useUsers();
  const userOptions = tenantUsers.map((u) => ({ id: u.id, name: u.name, email: u.email }));
  const classificationOptions = (contentV3?.forms ?? [])
    .filter((f) => f.classificationKey)
    .map((f) => ({ key: f.classificationKey, label: f.name || f.classificationKey }));
  const constantNames = useMemo(() => Object.keys(contentV3?.constants ?? {}), [contentV3?.constants]);
  const lineItemGroups: RowCheckGroupOption[] = useMemo(
    () =>
      (jobType?.lineItemDefinitions ?? []).map((d) => ({
        groupId: d.groupId,
        name: d.name,
        columns: (Array.isArray(d.columns) ? d.columns : [])
          .map((c) =>
            c && typeof c === 'object' && typeof (c as { name?: unknown }).name === 'string'
              ? (c as { name: string }).name
              : '',
          )
          .filter(Boolean),
      })),
    [jobType],
  );

  // F-056 — the field vocabulary is the SELECTED FORM's field bindings, never
  // the job-type union across forms (`useJobTypeFields` reads the flattened
  // legacy content, which merges every form). The union is offered only while
  // the V3 content hasn't loaded yet — a state that cannot create anyway
  // (`replaceAutomationRules` needs the form).
  const formFieldSummaries: FieldDefinitionSummary[] = useMemo(
    () => (selectedForm ? summarizeFormFields(selectedForm.fields) : jobTypeFields),
    [selectedForm, jobTypeFields],
  );
  const scopeFields = useMemo(
    () => formFieldSummaries.map((f) => ({ name: f.name, label: f.label || f.name })),
    [formFieldSummaries],
  );

  // ── Flow state ─────────────────────────────────────────────────────────
  // A `?effect=&field=` hand-off (field editor's "Add a custom check" bridge)
  // pre-answers WHERE + ON WHAT from the effect's registry defaults and lands
  // on IF/THEN with the effect already stacked.
  const effectParam = searchParams.get('effect');
  const fieldParam = searchParams.get('field') ?? undefined;
  const handedOff = effectParam ? effectByKey(effectParam) : undefined;

  const [moment, setMoment] = useState<MomentId | null>(handedOff?.defaultMoment ?? null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [scope, setScope] = useState<ScopeId | null>(handedOff?.defaultScope ?? null);
  const [fieldName, setFieldName] = useState<string | undefined>(
    handedOff?.defaultScope === 'field' ? (fieldParam ?? scopeFields[0]?.name) : undefined,
  );
  const [condition, setCondition] = useState<ConditionGroup | undefined>(undefined);
  const [branches, setBranches] = useState<AutomationBranch[]>(
    handedOff ? seedBranchesForEffects([handedOff.key], handedOff.defaultMoment) : [],
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [screen, setScreen] = useState<number>(handedOff ? S_IFTHEN : S_WHERE);
  const [error, setError] = useState<string | null>(null);

  const whereRows = whereOptions();
  const activeRow: WhereOption | undefined = moment
    ? whereRows.find((r) => r.key === rowKeyForMoment(moment))
    : undefined;
  const scopes = moment ? scopeOptions(moment) : [];
  const palette = moment && scope ? effectPalette(moment, scope) : [];
  // E-065 — collapsed-palette banner data (≤2 enabled here, a richer scope exists).
  const collapse = moment && scope ? scopeCollapseHint(moment, scope) : null;

  const routeBranch = branches.find((b): b is AutomationBranchRoute => b.family === 'route');
  const checkBranches = branches.filter((b): b is AutomationBranchCheck => b.family === 'check');
  const selectedKeys = new Set<string>([
    ...checkBranches.map((b) => b.effectKey),
    ...(routeBranch?.actions.map((a) => a.effectKey) ?? []),
  ]);

  const assembled: Automation | null = moment && scope
    ? {
        groupId: null,
        name,
        description,
        isActive: true,
        scope,
        fieldName: scope === 'field' ? fieldName : undefined,
        moment,
        stages: stages.length > 0 ? stages : undefined,
        branches: branches.map((b) => ({ ...b, condition })),
      }
    : null;

  // ── The running rule-sentence ──────────────────────────────────────────
  function wherePhrase(): string {
    if (!moment || !activeRow) return 'Pick where the automation fires';
    if (moment === 'step_enter') {
      return stages.length ? `Entering ${stageLabelList(stages)}` : 'Entering any step';
    }
    if (moment === 'on_validate') {
      return stages.length
        ? `When accepted at ${stageLabelList(stages)}`
        : 'When accepted at any step';
    }
    return activeRow.label;
  }
  const sentence =
    assembled && assembled.branches.length > 0
      ? describeAutomation(assembled)
      : `${wherePhrase()} — …`;

  // ── Screen 1 helpers ───────────────────────────────────────────────────
  function pickWhere(row: WhereOption) {
    if (!row.enabled || row.moment === null) return;
    setMoment(row.moment);
    setStages([]);
    // Scope + palette derive from the moment — reset the downstream answers.
    setScope(null);
    setFieldName(undefined);
    setBranches([]);
  }

  function toggleStepChip(stepType: string) {
    const stage = stageForStep(stepType);
    setStages((prev) => (prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]));
  }

  // ── Screen 2 helpers ───────────────────────────────────────────────────
  function pickScope(next: ScopeId) {
    setScope(next);
    setFieldName(next === 'field' ? (fieldName ?? scopeFields[0]?.name) : undefined);
    setBranches([]); // the palette re-derives
  }

  function changeForm(nextFormId: string) {
    setPickedFormId(nextFormId);
    if (scope === 'field') {
      const nextFields = contentV3?.forms.find((f) => f.id === nextFormId)?.fields ?? [];
      setFieldName(nextFields[0]?.name);
    }
  }

  // ── Screen 3 helpers ───────────────────────────────────────────────────
  function toggleEffect(key: string) {
    if (!moment) return;
    const effect = effectByKey(key);
    if (!effect) return;
    if (effect.bucket === 'fill_transform') {
      // Different editor + storage (field-level behaviors) — hand off through
      // the same pending-draft signal the AutomationsTab palette uses.
      setPendingAutomationDraft({
        formId,
        fieldName: fieldName ?? scopeFields[0]?.name ?? '',
        effectKey: key,
      });
      navigate(`/admin/job-types/${jobTypeId}?tab=automations`);
      return;
    }
    if (effect.bucket === 'check') {
      setBranches((prev) => {
        const existing = prev.find((b) => b.family === 'check' && b.effectKey === key);
        if (existing) return prev.filter((b) => b !== existing);
        return [...prev, { family: 'check', effectKey: key, severity: 'error', config: seedConfigForEffect(key, moment) }];
      });
      return;
    }
    // route — compose into ONE route branch (stacking).
    setBranches((prev) => {
      const rb = prev.find((b): b is AutomationBranchRoute => b.family === 'route');
      if (!rb) {
        return [...prev, ...seedBranchesForEffects([key], moment)];
      }
      const has = rb.actions.some((a) => a.effectKey === key);
      const actions = has
        ? rb.actions.filter((a) => a.effectKey !== key)
        : [...rb.actions, { effectKey: key, config: seedConfigForEffect(key, moment) }];
      const rest = prev.filter((b) => b !== rb);
      return actions.length === 0 ? rest : [...rest, { ...rb, actions }];
    });
  }

  function updateCheckBranch(index: number, next: AutomationBranchCheck) {
    setBranches((prev) => prev.map((b) => (b === checkBranches[index] ? next : b)));
  }

  function updateRouteAction(actionIndex: number, config: Record<string, unknown>) {
    if (!routeBranch) return;
    const actions = routeBranch.actions.slice();
    const current = actions[actionIndex];
    if (!current) return;
    actions[actionIndex] = { ...current, config };
    setBranches((prev) => prev.map((b) => (b === routeBranch ? { ...routeBranch, actions } : b)));
  }

  // P2b — a row_check's IF references the group's columns as dotted refs.
  // The plain-field vocabulary is form-scoped (F-056).
  const rowCheckGroupId = checkBranches.find(
    (b) => b.effectKey === 'row_check' && typeof b.config.rowGroup === 'string',
  )?.config.rowGroup as string | undefined;
  const conditionFields: FieldDefinitionSummary[] = useMemo(() => {
    if (!rowCheckGroupId) return formFieldSummaries;
    const group = lineItemGroups.find((g) => g.groupId === rowCheckGroupId);
    if (!group) return formFieldSummaries;
    const dotted: FieldDefinitionSummary[] = group.columns.map((col) => ({
      id: `${group.groupId}.${col}`,
      name: `${group.groupId}.${col}`,
      label: `${group.name} › ${col}`,
      dataType: 'text',
      groupId: group.groupId,
      groupName: group.name,
    }));
    return [...dotted, ...formFieldSummaries];
  }, [rowCheckGroupId, lineItemGroups, formFieldSummaries]);

  // ── Review / create ────────────────────────────────────────────────────
  const returnTo = searchParams.get('returnTo');
  const backHref = returnTo ?? `/admin/job-types/${jobTypeId}?tab=automations`;
  const backLabel = returnTo ? '← Back to Fields' : '← Back to Automations';

  function finish() {
    if (!assembled || !name.trim()) return;
    let inputs: ReturnType<typeof compileAutomation>;
    try {
      inputs = compileAutomation({ ...assembled, name, description });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create this automation — it's not valid yet.");
      return;
    }
    const ids = replaceAutomationRules(formId, [], inputs);
    if (ids.length === 0) {
      setError("Couldn't create this automation — the form it belongs to is missing.");
      return;
    }
    const panelHref = `/admin/job-types/${jobTypeId}/automations/${assembled.groupId ?? ids[0]}?form=${formId}`;
    if (returnTo) {
      navigate(returnTo);
      toast.info('Automation added.', {
        label: 'Stay in automations',
        onClick: () => navigate(panelHref),
      });
      return;
    }
    navigate(panelHref);
  }

  const canLeaveWhere = moment !== null && (activeRow?.enabled ?? false);
  const canLeaveScope = scope !== null && (scope !== 'field' || Boolean(fieldName));
  const canLeaveIfThen = branches.length > 0;
  const nextEnabled =
    screen === S_WHERE ? canLeaveWhere : screen === S_SCOPE ? canLeaveScope : canLeaveIfThen;

  return (
    <div className={panelStyles.page}>
      <Link to={backHref} className={panelStyles.backLink} data-testid="create-back-link">
        {backLabel}
      </Link>

      <h1 className={styles.title}>New automation</h1>

      <div className={styles.progressDots} data-testid="create-progress">
        {SCREEN_LABELS.map((label, i) => (
          <span key={label} className={styles.dot} data-active={i === screen} data-done={i < screen}>
            {label}
          </span>
        ))}
      </div>

      <div className={styles.sentenceBar} data-testid="rule-sentence">
        {sentence}
      </div>

      {error && <FormErrorInline error={new Error(error)} />}

      {screen === S_WHERE && (
        <div className={panelStyles.section} data-testid="where-screen">
          <span className={panelStyles.sectionTitle} id="where-label">
            Where does it fire?
          </span>
          <div className={panelStyles.sectionBody} role="radiogroup" aria-labelledby="where-label">
            {whereRows.map((row) => {
              const selected = moment !== null && row.moment === moment && row.key === rowKeyForMoment(moment);
              return (
                <div key={row.key} className={styles.whereRow} data-testid={`where-row-${row.key}`}>
                  <label className={styles.whereLabel} data-disabled={!row.enabled}>
                    <input
                      type="radio"
                      name="create-where"
                      checked={selected}
                      disabled={!row.enabled}
                      onChange={() => pickWhere(row)}
                    />
                    {row.label}
                  </label>
                  {row.hint && (
                    <span className={styles.lifecycleHint} data-testid={`where-hint-${row.key}`}>
                      {row.hint}
                    </span>
                  )}
                  {!row.enabled && row.disabledReason && (
                    <span className={styles.disabledReason}>{row.disabledReason}</span>
                  )}
                  {selected && row.stepChips && (
                    <div className={styles.chipRow}>
                      {row.anyStepGhost && (
                        <button
                          type="button"
                          className={styles.ghostChip}
                          data-testid="where-any-step-chip"
                          aria-pressed={stages.length === 0}
                          onClick={() => setStages([])}
                        >
                          any step
                        </button>
                      )}
                      {chipSteps.map((s) => (
                        <button
                          key={s.stepType}
                          type="button"
                          className={styles.chip}
                          data-testid={`where-step-chip-${s.stepType}`}
                          aria-pressed={stages.includes(stageForStep(s.stepType))}
                          onClick={() => toggleStepChip(s.stepType)}
                        >
                          {s.label || STEP_LABELS[s.stepType as keyof typeof STEP_LABELS] || s.stepType}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {screen === S_SCOPE && moment && (
        <div className={panelStyles.section} data-testid="scope-screen">
          {(contentV3?.forms.length ?? 0) > 1 && (
            <>
              <label className={panelStyles.sectionTitle} htmlFor="create-form-select">
                Form
              </label>
              <div className={panelStyles.sectionBody}>
                <select
                  id="create-form-select"
                  data-testid="create-form-select"
                  className={panelStyles.textInput}
                  value={formId}
                  onChange={(e) => changeForm(e.target.value)}
                >
                  {contentV3!.forms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          <span className={panelStyles.sectionTitle} id="onwhat-label">
            On what?
          </span>
          {/* E-065 part 3 — acts-on vs looks-at, said at the decision point. */}
          <p className={styles.teachingLine} data-testid="scope-teaching">
            Your conditions can test any field regardless of what the rule acts on.
          </p>
          <div className={panelStyles.sectionBody} role="radiogroup" aria-labelledby="onwhat-label">
            {scopes.map((o) => (
              <div key={o.scope} className={styles.whereRow} data-testid={`scope-option-${o.scope}`}>
                <label className={styles.whereLabel} data-disabled={!o.enabled}>
                  <input
                    type="radio"
                    name="create-scope"
                    checked={scope === o.scope}
                    disabled={!o.enabled}
                    onChange={() => pickScope(o.scope)}
                  />
                  {o.label}
                  {/* E-065 part 1 — each scope reveals its consequences here. */}
                  {o.enabled && (
                    <span className={styles.scopeCount}>
                      — enables {o.enabledCount} action{o.enabledCount === 1 ? '' : 's'} here
                    </span>
                  )}
                </label>
                {!o.enabled && o.disabledReason && (
                  <span className={styles.disabledReason}>{o.disabledReason}</span>
                )}
              </div>
            ))}
            {scope === 'field' && (
              <select
                data-testid="create-field-select"
                className={panelStyles.textInput}
                value={fieldName ?? ''}
                onChange={(e) => setFieldName(e.target.value)}
              >
                <option value="" disabled>
                  Select a field…
                </option>
                {scopeFields.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label || f.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {screen === S_IFTHEN && moment && scope && (
        <div className={styles.ifThen} data-testid="ifthen-screen">
          <div className={panelStyles.section}>
            <span className={panelStyles.sectionTitle}>If</span>
            <div className={panelStyles.sectionBody}>
              <ConditionGroupEditor
                group={condition ?? emptyConditionGroup()}
                fields={conditionFields}
                lookupTables={lookupTables}
                constants={constantNames}
                compact
                onChange={(group) => setCondition(isConditionEmpty(group) ? undefined : group)}
              />
            </div>
          </div>

          <div className={panelStyles.section}>
            <span className={panelStyles.sectionTitle}>Then</span>
            <div className={panelStyles.sectionBody}>
              {/* E-065 part 2 — a collapsed palette says WHY and points back. */}
              {collapse && (
                <div className={styles.collapseBanner} data-testid="palette-collapse-banner">
                  Most actions at this moment operate on {collapse.label.toLowerCase()} —{' '}
                  <button
                    type="button"
                    className={styles.collapseBannerLink}
                    onClick={() => setScreen(S_SCOPE)}
                  >
                    choose &lsquo;{collapse.label}&rsquo;
                  </button>{' '}
                  to unlock {collapse.unlockCount} more.
                </div>
              )}
              <div className={styles.chipRow}>
                {palette.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className={styles.chip}
                    data-testid={`palette-chip-${entry.key}`}
                    aria-pressed={selectedKeys.has(entry.key)}
                    disabled={!entry.enabled}
                    title={entry.enabled ? entry.description : entry.disabledReason}
                    onClick={() => toggleEffect(entry.key)}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              {checkBranches.map((branch, i) => (
                <div key={branch.effectKey} className={styles.effectForm} data-testid={`effect-form-${branch.effectKey}`}>
                  <div className={styles.effectFormHeader}>
                    <span>{effectByKey(branch.effectKey)?.label ?? branch.effectKey}</span>
                    <label className={styles.severityLabel}>
                      Severity
                      <select
                        data-testid={`effect-severity-${branch.effectKey}`}
                        value={branch.severity}
                        onChange={(e) =>
                          updateCheckBranch(i, { ...branch, severity: e.target.value as 'error' | 'warning' })
                        }
                      >
                        <option value="error">Block the operator</option>
                        <option value="warning">Warn the operator</option>
                      </select>
                    </label>
                  </div>
                  <CheckConfigBody
                    effectKey={branch.effectKey}
                    config={branch.config}
                    onChange={(config) => updateCheckBranch(i, { ...branch, config })}
                    fieldNames={formFieldSummaries.map((f) => f.name)}
                    lineItemGroups={lineItemGroups}
                  />
                </div>
              ))}

              {routeBranch?.actions.map((action, actionIndex) => (
                <div
                  key={`${action.effectKey}-${actionIndex}`}
                  className={styles.effectForm}
                  data-testid={`effect-form-${action.effectKey}`}
                >
                  <div className={styles.effectFormHeader}>
                    <span>{effectByKey(action.effectKey)?.label ?? action.effectKey}</span>
                  </div>
                  <RouteConfigBody
                    effectKey={action.effectKey}
                    config={action.config}
                    pipelineSteps={pipelineSteps}
                    fieldNames={formFieldSummaries.map((f) => f.name)}
                    roles={roleOptions}
                    users={userOptions}
                    classifications={classificationOptions}
                    onChange={(config) => updateRouteAction(actionIndex, config)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {screen === S_REVIEW && assembled && (
        <div className={styles.review} data-testid="review-screen">
          <IdentitySection
            automation={{ ...assembled, name, description }}
            onChange={(next) => {
              setName(next.name);
              setDescription(next.description);
            }}
          />
          <TryItSection
            automation={{ ...assembled, name: name || 'Untitled', description }}
            formFields={scopeFields}
            stage={assembled.stages?.[0]}
            versionId={versionId}
          />
        </div>
      )}

      <div className={styles.navRow}>
        {screen > S_WHERE ? (
          <button type="button" className={styles.navBtn} onClick={() => { setError(null); setScreen((s) => s - 1); }}>
            Back
          </button>
        ) : (
          <span />
        )}
        {screen < S_REVIEW ? (
          <button
            type="button"
            className={styles.navBtnPrimary}
            disabled={!nextEnabled}
            onClick={() => setScreen((s) => s + 1)}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className={styles.navBtnPrimary}
            data-testid="create-finish"
            disabled={!name.trim()}
            onClick={finish}
          >
            Create automation
          </button>
        )}
      </div>
    </div>
  );
}
